import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Mic } from "lucide-react";
import { Button } from "../../ui";
import { cn } from "../../ui/utils/cn";
import { ChatTextarea } from "../agent_chat/ChatTextarea";
import { VoiceRecorder } from "../agent_chat/VoiceRecorder";
import {
  useStartContributionMutation,
  useSendContributionMessageMutation,
  useSendContributionVoiceMutation,
  useSubmitContributionMutation,
  type ChatMessageView,
  type ContributionSummary,
  type TurnChoice,
  type TurnResponse,
} from "../../services/rtkApi/contributeApi";

// The Idea Desk chatbot. A logged-in member pitches an idea (typed or spoken), the
// host runs a short, warm, NON-technical back-and-forth (offering tappable choices),
// then shows a summary to confirm and submit. Reuses the agent-chat recorder +
// auto-growing textarea so it feels native to the site.

const INPUT_CLS =
  "w-full border-brutal border-ink bg-background px-3 py-2 text-sm rounded-none shadow-inset " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 " +
  "max-[700px]:text-[16px]";

function Bubble({ m }: { m: ChatMessageView }) {
  const isUser = m.role === "user";
  return (
    <div className={cn("flex w-full", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[80%] border-2 border-ink rounded-sm px-3 py-2 text-sm",
          isUser ? "bg-sky/20" : "bg-mint/20"
        )}
        style={{ boxShadow: "2px 2px 0px rgba(0,0,0,0.15)" }}
      >
        {!isUser && <div className="text-[10px] font-bold uppercase text-ink/50 mb-0.5">idea desk</div>}
        {m.voice && (
          <div className="text-[10px] text-ink/50 mb-0.5 flex items-center gap-1">
            <Mic className="w-3 h-3" /> voice
          </div>
        )}
        <div className="whitespace-pre-wrap break-words">{m.content}</div>
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex justify-start">
      <div className="border-2 border-ink rounded-sm px-3 py-2 bg-mint/20" style={{ boxShadow: "2px 2px 0px rgba(0,0,0,0.15)" }}>
        <span className="inline-flex gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-ink/50 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-ink/50 animate-bounce" style={{ animationDelay: "120ms" }} />
          <span className="w-1.5 h-1.5 rounded-full bg-ink/50 animate-bounce" style={{ animationDelay: "240ms" }} />
        </span>
      </div>
    </div>
  );
}

export default function ContributeChat({ onGoToMyIdeas }: { onGoToMyIdeas?: () => void }) {
  const [start, { isLoading: starting }] = useStartContributionMutation();
  const [sendMessage] = useSendContributionMessageMutation();
  const [sendVoice] = useSendContributionVoiceMutation();
  const [submit, { isLoading: submitting }] = useSubmitContributionMutation();

  const [contributionId, setContributionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessageView[]>([]);
  const [choices, setChoices] = useState<TurnChoice[]>([]);
  const [allowFreeText, setAllowFreeText] = useState(true);
  const [summary, setSummary] = useState<ContributionSummary | null>(null);
  const [canSubmit, setCanSubmit] = useState(false);
  const [dismissedSummary, setDismissedSummary] = useState(false);

  const [input, setInput] = useState("");
  const [voiceMode, setVoiceMode] = useState(false);
  const [voiceSending, setVoiceSending] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const [editTitle, setEditTitle] = useState("");
  const [editPitch, setEditPitch] = useState("");

  const scrollRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);

  // keep the summary editor in sync with the latest proposed summary.
  useEffect(() => {
    if (summary) {
      setEditTitle(summary.title);
      setEditPitch(summary.pitch);
    }
  }, [summary]);

  // auto-scroll to the newest message / typing indicator.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, thinking, canSubmit, submitted]);

  // open (or resume) a session once on mount.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const c = await start().unwrap();
        setContributionId(c.id);
        const chat = c.chat || [];
        setMessages(chat);
        const lastAssistant = [...chat].reverse().find((m) => m.role === "assistant");
        setChoices(lastAssistant?.choices || []);
        if (c.title || c.pitch) {
          setSummary({ title: c.title, pitch: c.pitch });
          setCanSubmit(true);
        }
        if (c.status !== "draft") setSubmitted(true);
      } catch (e: unknown) {
        const msg = (e as { data?: { error?: string } })?.data?.error || "couldn't open the idea desk — try again in a bit";
        setStartError(msg);
      }
    })();
  }, [start]);

  // apply an assistant turn to local state.
  function applyTurn(turn: TurnResponse, opts?: { appendUserMessage?: boolean }) {
    setMessages((prev) => {
      const next = [...prev];
      if (opts?.appendUserMessage && turn.userMessage) next.push(turn.userMessage);
      next.push(turn.message);
      return next;
    });
    setChoices(turn.message.choices || []);
    setAllowFreeText(turn.allowFreeText);
    setSummary(turn.summary);
    setCanSubmit(turn.canSubmit);
    setDismissedSummary(false);
  }

  async function sendText(text: string, opts?: { echo?: boolean }) {
    const id = contributionId;
    if (!id || !text.trim() || thinking) return;
    if (opts?.echo !== false) {
      setMessages((prev) => [...prev, { role: "user", content: text, at: new Date().toISOString() }]);
    }
    setInput("");
    setChoices([]);
    setThinking(true);
    try {
      const turn = await sendMessage({ id, text }).unwrap();
      applyTurn(turn); // user bubble already shown optimistically
    } catch (e: unknown) {
      const msg = (e as { data?: { error?: string } })?.data?.error || "something went wrong — try again";
      toast.error(msg);
    } finally {
      setThinking(false);
    }
  }

  function handleChoice(c: TurnChoice) {
    sendText(c.value || c.label);
  }

  async function handleVoiceSend(audio: Blob, durationMs: number) {
    const id = contributionId;
    if (!id) return;
    setVoiceSending(true);
    setThinking(true);
    setChoices([]);
    try {
      const turn = await sendVoice({ id, audio, durationMs }).unwrap();
      applyTurn(turn, { appendUserMessage: true });
      setVoiceMode(false);
    } catch (e: unknown) {
      const msg = (e as { data?: { error?: string } })?.data?.error || "couldn't process that recording";
      toast.error(msg);
    } finally {
      setVoiceSending(false);
      setThinking(false);
    }
  }

  async function handleSubmit() {
    const id = contributionId;
    if (!id) return;
    try {
      await submit({ id, title: editTitle.trim(), pitch: editPitch.trim() }).unwrap();
      setSubmitted(true);
      setCanSubmit(false);
    } catch (e: unknown) {
      const msg = (e as { data?: { error?: string } })?.data?.error || "couldn't submit — try again";
      toast.error(msg);
    }
  }

  const showSummaryCard = canSubmit && !dismissedSummary && !submitted;

  return (
    <div className="flex flex-col h-[68vh] max-h-[660px] min-h-[420px] border-brutal border-ink rounded-sm bg-paper shadow-brutal-md overflow-hidden">
      <div className="px-4 py-2 border-b-2 border-ink bg-lemon/30 flex items-center justify-between">
        <div className="font-bold text-sm">💡 Idea Desk</div>
        <div className="text-[10px] text-ink/50">pitch a feature for n450s</div>
      </div>

      {/* messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
        {starting && messages.length === 0 && <TypingBubble />}
        {startError && (
          <div className="text-sm text-red-600 border-2 border-red-300 bg-red-50 rounded-sm p-3">{startError}</div>
        )}
        {messages.map((m, i) => (
          <Bubble key={i} m={m} />
        ))}
        {thinking && <TypingBubble />}
      </div>

      {/* summary confirm card */}
      {showSummaryCard && (
        <div className="border-t-2 border-ink bg-lemon/15 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase text-ink/60">here's your idea — tweak &amp; submit</div>
          <input
            className={INPUT_CLS}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            placeholder="title"
          />
          <textarea
            className={cn(INPUT_CLS, "resize-none")}
            rows={3}
            value={editPitch}
            onChange={(e) => setEditPitch(e.target.value)}
            placeholder="describe your idea"
          />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleSubmit} loading={submitting} disabled={!editTitle.trim() && !editPitch.trim()}>
              submit idea ✨
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDismissedSummary(true)} disabled={submitting}>
              keep refining
            </Button>
          </div>
        </div>
      )}

      {/* submitted state */}
      {submitted && (
        <div className="border-t-2 border-ink bg-mint/25 p-4 text-center space-y-2">
          <div className="text-base font-bold">Got it — thank you! 🙌</div>
          <div className="text-sm text-ink/80">
            Your idea is on the desk. Track it under “My ideas”. If it gets built, it'll show as your contribution — plus
            respect and a Contributor badge.
          </div>
          {onGoToMyIdeas && (
            <Button size="sm" variant="sky" onClick={onGoToMyIdeas}>
              see my ideas
            </Button>
          )}
        </div>
      )}

      {/* choices + input */}
      {!submitted && !startError && (
        <div className="border-t-2 border-ink p-3 space-y-2 bg-paper">
          {choices.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {choices.map((c, i) => (
                <Button key={i} size="sm" variant="outline" onClick={() => handleChoice(c)} disabled={thinking}>
                  {c.label}
                </Button>
              ))}
            </div>
          )}

          {voiceMode ? (
            <VoiceRecorder
              onSend={handleVoiceSend}
              onCancel={() => setVoiceMode(false)}
              sending={voiceSending}
              sendLabel="use recording"
            />
          ) : (
            allowFreeText && (
              <div className="flex items-end gap-2">
                <button
                  type="button"
                  onClick={() => setVoiceMode(true)}
                  disabled={thinking || !contributionId}
                  className="shrink-0 h-9 w-9 flex items-center justify-center border-brutal border-ink rounded-sm bg-background hover:bg-accent disabled:opacity-50"
                  aria-label="record your idea"
                  title="record"
                >
                  <Mic className="w-4 h-4" />
                </button>
                <ChatTextarea
                  value={input}
                  onChange={setInput}
                  onSubmit={() => sendText(input)}
                  placeholder="type your idea…"
                  disabled={thinking || !contributionId}
                />
                <Button size="sm" onClick={() => sendText(input)} loading={thinking} disabled={!input.trim() || !contributionId}>
                  send
                </Button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
